/* eslint-disable react/prop-types */
import { useEffect, useState, useMemo } from "react";
import axios from "../../../api/axios";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DataTable from "examples/Tables/DataTable";

export default function TaxClaimsV2() {
  const [pfno, setPfno] = useState("");
  const [taxYear, setTaxYear] = useState(new Date().getFullYear());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    deduction_type_id: "",
    amount: "",
    notes: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/tax/staff-claims", {
        params: { pf_no: pfno || undefined, tax_year: taxYear },
      });
      setRows(res.data?.data || []);
    } finally {
      setLoading(false);
    }
  };

  const createDraft = async () => {
    if (!pfno || !form.deduction_type_id) return alert("PF No and type required");

    await axios.post("/tax/staff-claims", {
      pf_no: pfno,
      tax_year: taxYear,
      deduction_type_id: Number(form.deduction_type_id),
      amount: Number(form.amount || 0),
      notes: form.notes,
      status: "draft",
    });

    await load();
  };

  const uploadEvidence = async (claimId, file) => {
    const fd = new FormData();
    fd.append("file", file);

    await axios.post(`/tax/claims/${claimId}/evidence`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    await load();
  };

  const submit = async (id) => {
    await axios.post(`/tax/claims/${id}/submit`);
    await load();
  };

  const approve = async (id) => {
    await axios.post(`/tax/staff-claims/${id}/approve`);
    await load();
  };

  const reject = async (id) => {
    const reason = window.prompt("Reason for rejection:");
    if (!reason) return;
    await axios.post(`/tax/staff-claims/${id}/reject`, { reason });
    await load();
  };

  // ✅ FIX: use Cell: (cell) => ... (no {row} destructuring)
  const columns = useMemo(
    () => [
      { Header: "ID", accessor: "id" },
      { Header: "PFNO", accessor: "pfno" },
      { Header: "Year", accessor: "tax_year" },
      { Header: "Type", accessor: "type.name" },
      { Header: "Amount", accessor: "amount" },
      { Header: "Status", accessor: "status" },
      {
        Header: "Evidence",
        accessor: "evidence",
        disableSortBy: true,
        Cell: (cell) => {
          const r = cell?.row?.original || {};
          const isLocked = ["approved", "rejected"].includes(r.status);

          return (
            <MDBox>
              <MDButton
                size="small"
                color="dark"
                component="label"
                disabled={isLocked || loading || !r?.id}
              >
                Upload
                <input
                  type="file"
                  hidden
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    // allow re-uploading same file by resetting value
                    e.target.value = "";
                    if (f && r?.id) uploadEvidence(r.id, f);
                  }}
                />
              </MDButton>

              <MDTypography variant="caption" color="text" sx={{ ml: 1 }}>
                {Array.isArray(r.attachments) && r.attachments.length
                  ? `${r.attachments.length} file(s)`
                  : "none"}
              </MDTypography>
            </MDBox>
          );
        },
      },
      {
        Header: "Actions",
        accessor: "actions",
        disableSortBy: true,
        Cell: (cell) => {
          const r = cell?.row?.original || {};

          return (
            <MDBox display="flex" gap={1} flexWrap="wrap">
              {r.status === "draft" && (
                <MDButton
                  size="small"
                  color="info"
                  onClick={() => submit(r.id)}
                  disabled={loading || !r?.id}
                >
                  Submit
                </MDButton>
              )}

              {r.status === "submitted" && (
                <>
                  <MDButton
                    size="small"
                    color="success"
                    onClick={() => approve(r.id)}
                    disabled={loading || !r?.id}
                  >
                    Approve
                  </MDButton>
                  <MDButton
                    size="small"
                    color="error"
                    onClick={() => reject(r.id)}
                    disabled={loading || !r?.id}
                  >
                    Reject
                  </MDButton>
                </>
              )}
            </MDBox>
          );
        },
      },
    ],
    [loading, pfno, taxYear] // safe deps (upload/approve/submit already closes over latest load)
  );

  return (
    <MDBox p={3}>
      <MDTypography variant="h5">Tax Claims (Draft / Submit / Approve)</MDTypography>

      <MDBox mt={2} display="flex" gap={2} flexWrap="wrap">
        <MDBox width={200}>
          <MDInput label="PFNO" value={pfno} onChange={(e) => setPfno(e.target.value)} />
        </MDBox>
        <MDBox width={140}>
          <MDInput
            label="Tax Year"
            type="number"
            value={taxYear}
            onChange={(e) => setTaxYear(Number(e.target.value))}
          />
        </MDBox>
        <MDButton color="secondary" onClick={load} disabled={loading}>
          {loading ? "Loading..." : "Load"}
        </MDButton>
      </MDBox>

      <MDBox mt={3} p={2} sx={{ border: "1px solid #eee", borderRadius: 2 }}>
        <MDTypography variant="h6">Create Draft Claim</MDTypography>

        <MDBox mt={1} display="flex" gap={2} flexWrap="wrap">
          <MDBox width={200}>
            <MDInput
              label="Deduction Type ID"
              value={form.deduction_type_id}
              onChange={(e) => setForm({ ...form, deduction_type_id: e.target.value })}
            />
          </MDBox>

          <MDBox width={160}>
            <MDInput
              label="Amount"
              type="number"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
            />
          </MDBox>

          <MDBox width={260}>
            <MDInput
              label="Notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </MDBox>

          <MDButton color="info" onClick={createDraft} disabled={loading}>
            Save Draft
          </MDButton>
        </MDBox>
      </MDBox>

      <MDBox mt={3}>
        <DataTable
          table={{ columns, rows }}
          entriesPerPage={false}
          showTotalEntries={false}
          canSearch
          pagination
          noEndBorder
        />
      </MDBox>
    </MDBox>
  );
}
